import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

Vue.use(VueRouter);

const requireLogin = (to, from, next) => {
  const loginBool = store.state.isLogin;

  // 특정 경로에 대해서는 접근을 항상 허용
  const publicPaths = ["/changePassword", "/"];

  // Public path에 대한 접근 허용
  if (publicPaths.includes(to.path)) {
    next();
    return;
  }

  // 로그인 여부 체크
  if (!loginBool) {
    // 로그인하지 않은 경우 홈(로그인 페이지)으로 리다이렉트
    if (to.path !== "/" && to.path !== "/changePassword") {
      next("/");
      return;
    }
    // 이미 홈이나 changePassword 페이지에 있으면 통과
    next();
    return;
  }

  // 로그인한 사용자만 메뉴 권한 체크 수행
  // 유저 메뉴에 따른 접근 권한 체크
  const menu = store.state.menu || "";
  const userMenu = menu ? menu.split(",") : [];
  const accessiblePaths = {
    // 서비스
    대시보드: "/service/dashboard/main",
    정산: "/service/accounts/main",
    통계: "/service/statistics/main",
    마이페이지: "/service/mypage/main",
    // 어드민
    계약: "/admin/contract",
    "계약자 관리": "/admin/contract/contractors",
    "계약 관리": "/admin/contract/contracts",
    "앨범/음원": "/admin/contents",
    "앨범 관리": "/admin/contents/album",
    "음원 관리": "/admin/contents/song",
    "정산 자료 업로드": "/admin/excel/upload",
    "정산 대상금 확정": "/admin/settlement",
    "권한 관리": "/admin/role",
    "공지사항 관리": "/admin/notice",
    매출: "/admin/sales",
    월별: "/admin/sales/month",
    계약자별: "/admin/sales/contractors",
    아티스트별: "/admin/sales/artists",
    앨범별: "/admin/sales/albums",
    곡별: "/admin/sales/songs",
    "프로모션 신청서 관리": "/admin/promotion",
  };

  const allowedPaths = userMenu.map((menu) => accessiblePaths[menu]);

  // 현재 경로가 allowedPaths에 포함되어 있는지 또는 하위 경로인지 확인
  const isAllowed = allowedPaths.some((path) => {
    if (to.path === path || to.path.startsWith(path + "/")) {
      return true;
    }
    return false;
  });

  // menu가 비어있으면 사용자 정보가 아직 로드되지 않은 상태
  // 이 경우 사용자 정보를 먼저 로드한 후 다시 체크
  if (!menu && loginBool && to.path !== "/not-found") {
    // 사용자 정보 로드 후 다시 권한 체크
    store.dispatch("getAccountInfo")
      .then(() => {
        // 사용자 정보 로드 후 다시 권한 체크
        const currentMenu = store.state.menu || "";
        const currentUserMenu = currentMenu ? currentMenu.split(",") : [];
        const currentAllowedPaths = currentUserMenu.map((m) => accessiblePaths[m]);
        const currentIsAllowed = currentAllowedPaths.some((path) => {
          if (to.path === path || to.path.startsWith(path + "/")) {
            return true;
          }
          return false;
        });
        
        if (currentIsAllowed) {
          next();
        } else {
          next("/not-found");
        }
      })
      .catch(() => {
        // 사용자 정보 로드 실패 시 not-found로 이동
        next("/not-found");
      });
    return; // 비동기 처리 중이므로 여기서 종료
  }

  if (isAllowed) {
    next();
  } else if (to.path !== "/not-found") {
    next("/not-found"); // 접속할 수 없는 페이지로 이동 시 NotFound 컴포넌트로 이동
  } else {
    next(); // 이미 not-found 경로일 경우 무한 루프 방지
  }
};

const routes = [
  {
    path: "/changePassword",
    name: "changePassword",
    component: () => import("../views/account/ChangePassword.vue"),
  },
  {
    path: "/",
    name: "defaultLayout",
    component: () => import("../views/layout/Default.vue"),
    children: [
      {
        path: "",
        name: "home",
        component: () => import("../views/index.vue"),
      },
    ],
  },
  {
    path: "/service",
    name: "serviceLayout",
    beforeEnter: requireLogin,
    component: () => import("../views/layout/Dashboard.vue"),
    children: [
      {
        // 서비스 대시보드 (공지사항, 통계 등)
        path: "dashboard/main",
        name: "dashboardMain",
        component: () => import("../views/service/dashboard/Main.vue"),
      },
      {
        // 정산서
        path: "accounts/main",
        name: "accounts",
        component: () => import("../views/service/accounts/Main.vue"),
      },
      {
        // 통계
        path: "statistics/main",
        name: "statistics",
        component: () => import("../views/service/statistics/Main.vue"),
      },
      {
        // 마이페이지
        path: "mypage/main",
        name: "mypage",
        component: () => import("../views/service/mypage/Main.vue"),
      },
    ],
  },
  {
    path: "/admin",
    name: "admin",
    beforeEnter: requireLogin,
    component: () => import("../views/layout/Dashboard.vue"),
    children: [
      {
        // 계약자 관리
        path: "contract/contractors/manage",
        name: "contractorsManage",
        component: () => import("../views/admin/contract/ContractorManage.vue"),
      },
      {
        // 계약자 등록
        path: "contract/contractors/register/:id?",
        name: "contractorsRegister",
        component: () =>
          import("../views/admin/contract/ContractorRegister.vue"),
      },
      {
        // 계약 관리
        path: "contract/contracts/manage",
        name: "contractsManage",
        component: () => import("../views/admin/contract/ContractManage.vue"),
      },
      {
        // 계약 등록
        path: "contract/contracts/register/:id?",
        name: "contractsRegister",
        component: () => import("../views/admin/contract/ContractRegister.vue"),
      },
      {
        // 앨범 관리
        path: "contents/album/manage",
        name: "albumManage",
        component: () => import("../views/admin/contents/AlbumManage.vue"),
      },
      {
        // 앨범 등록
        path: "contents/album/register/:id?",
        name: "albumRegister",
        component: () => import("../views/admin/contents/AlbumRegister.vue"),
      },
      {
        // 음원 관리
        path: "contents/song/manage",
        name: "songManage",
        component: () => import("../views/admin/contents/SongManage.vue"),
      },
      {
        // 음원 등록
        path: "contents/song/register/:id?",
        name: "songRegister",
        component: () => import("../views/admin/contents/SongRegister.vue"),
      },
      {
        // 정산 자료 업로드
        path: "excel/upload",
        name: "excelUpload",
        component: () => import("../views/admin/excel/Manage.vue"),
      },
      {
        // 정산 대상금 확정 관리
        path: "settlement/manage",
        name: "settlementManage",
        component: () => import("../views/admin/settlement/Manage.vue"),
      },
      {
        // 권한 관리
        path: "role/manage",
        name: "roleManage",
        component: () => import("../views/admin/role/Manage.vue"),
      },
      {
        // 권한 등록
        path: "role/register/:id?",
        name: "roleRegister",
        component: () => import("../views/admin/role/Register.vue"),
      },
      {
        // 공지사항 관리
        path: "notice/manage",
        name: "noticeManage",
        component: () => import("../views/admin/notice/Manage.vue"),
      },
      {
        // 공지사항 등록
        path: "notice/register/:id?",
        name: "noticeRegister",
        component: () => import("../views/admin/notice/Register.vue"),
      },
      {
        // 매출 월별
        path: "sales/month/manage",
        name: "salesMonth",
        component: () => import("../views/admin/sales/Month.vue"),
      },
      {
        // 매출 계약자별
        path: "sales/contractors/manage",
        name: "salesContractors",
        component: () => import("../views/admin/sales/Contractors.vue"),
      },
      {
        // 매출 아티스트별
        path: "sales/artists/manage",
        name: "salesArtists",
        component: () => import("../views/admin/sales/Artists.vue"),
      },
      {
        // 매출 앨범별
        path: "sales/albums/manage",
        name: "salesAlbums",
        component: () => import("../views/admin/sales/Albums.vue"),
      },
      {
        // 매출 곡별
        path: "sales/songs/manage",
        name: "salesSongs",
        component: () => import("../views/admin/sales/Songs.vue"),
      },
      {
        // 프로모션 신청서 관리
        path: "promotion/manage",
        name: "promotionManage",
        component: () => import("../views/admin/promotion/Manage.vue"),
      },
      {
        // 프로모션 신청서 등록
        path: "promotion/register/:id?",
        name: "promotionRegister",
        component: () => import("../views/admin/promotion/Register.vue"),
      },
    ],
  },
  {
    path: "*",
    name: "notFound",
    component: () => import("../views/NotFound.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return { x: 0, y: 0 };
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach(requireLogin);

export default router;
